import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Loading from "../../Layouts/Loading";
import { fa_dateـetime, fa_number } from "../../utils/Persian";
import {
  type ExamHistoryChartItem,
  useExamHistoryChartsQuery,
} from "../../Redux/Apis/userExamApi";

type ChartType = "quant" | "verbal" | "general_new" | "general_old";

const chartConfigs: Record<
  ChartType,
  { label: string; description: string; color: string; valueLabel: string }
> = {
  quant: {
    label: "کوانت",
    description: "درصد درست کل سوالات آزمون‌های Quant (۰ تا ۱۰۰).",
    color: "#22c55e",
    valueLabel: "درصد درست",
  },
  verbal: {
    label: "وربال",
    description: "درصد درست کل سوالات آزمون‌های Verbal (۰ تا ۱۰۰).",
    color: "#ec4899",
    valueLabel: "درصد درست",
  },
  general_new: {
    label: "جامع جدید",
    description: "مجموع پرسنتایل‌های Verbal و Quant در آزمون‌های جامع جدید (۰ تا ۲۰۰).",
    color: "#f97316",
    valueLabel: "مجموع پرسنتایل",
  },
  general_old: {
    label: "جامع قدیم",
    description: "مجموع پرسنتایل‌های Verbal و Quant در آزمون‌های جامع قدیم (۰ تا ۲۰۰).",
    color: "#6366f1",
    valueLabel: "مجموع پرسنتایل",
  },
};

type ChartItemWithLabel = ExamHistoryChartItem & {
  dateLabel: string;
};

function ExamHistoryCharts() {
  const [activeType, setActiveType] = useState<ChartType>("quant");
  const { data, isLoading, isError } = useExamHistoryChartsQuery();

  const series: ChartItemWithLabel[] = useMemo(() => {
    if (!data) return [];
    const raw = data[activeType] ?? [];
    return raw
      .slice()
      .sort(
        (a, b) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      .map((item) => ({
        ...item,
        dateLabel: fa_dateـetime(item.date),
      }));
  }, [data, activeType]);

  const config = chartConfigs[activeType];

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: any[];
  }) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0].payload as ChartItemWithLabel;
    return (
      <div
        className="shadow-sm bg-white border rounded p-2 small"
        style={{ direction: "rtl", minWidth: 200 }}
      >
        <div className="fw-bold mb-1">{item.exam_name}</div>
        <div className="mb-1 text-muted">
          تاریخ آزمون: {fa_dateـetime(item.date)}
        </div>
        <div>
          {config.valueLabel}: {fa_number(item.value)}{" "}
          {activeType === "quant" || activeType === "verbal" ? "%" : ""}
        </div>
      </div>
    );
  };

  return (
    <main className="col-lg-9">
      <div className="panel-surface p-3 mb-3 d-flex align-items-center justify-content-between">
        <div>
          <h6 className="fw-bold m-0">نمودار پیشرفت آزمون‌ها</h6>
          <span className="panel-muted small">
            تاریخچه عملکرد شما در آزمون‌های Quant، Verbal و آزمون‌های جامع جدید/قدیم
          </span>
        </div>
      </div>

      <div className="panel-surface p-3">
        {isLoading && <Loading />}

        {!isLoading && isError && (
          <div className="text-center text-danger small py-4">
            در دریافت داده‌های نمودار مشکلی پیش آمد. لطفاً بعداً دوباره تلاش کنید.
          </div>
        )}

        {!isLoading && !isError && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {(Object.keys(chartConfigs) as ChartType[]).map((type) => {
                const cfg = chartConfigs[type];
                const isActive = activeType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 ${
                      isActive ? "btn-primary text-white" : "btn-light"
                    }`}
                    onClick={() => setActiveType(type)}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            <p className="small panel-muted mb-3">{config.description}</p>

            {series.length === 0 ? (
              <div className="text-center small panel-muted py-4">
                برای این نوع آزمون هنوز سابقه‌ای ثبت نشده است.
              </div>
            ) : (
              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer>
                  <BarChart data={series}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e5e7eb"
                    />
                    <XAxis
                      dataKey="dateLabel"
                      tick={{ fontSize: 11 }}
                      tickMargin={8}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value: number) =>
                        fa_number(Math.round(value))
                      }
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="value"
                      fill={config.color}
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default ExamHistoryCharts;

